/**
 * CertBadge — reusable employee certification badge card (blueprint §4).
 * Two data modes: live certs array (props.certs) or a sign-time snapshot (props.snapshot).
 * QR always encodes the public badge URL: {origin}/badge/{qrCode}.
 * Print-friendly: full variant fixed ~220px width, break-inside-avoid so
 * window.print() never slices a badge across pages.
 */
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { formatCalendarDate } from '../utils/calendarDate';

export interface BadgeCert {
  certType: string;
  certName: string;
  expirationDate: string | null;
}

export interface BadgeSnapshot {
  name: string;
  role: string;
  qrCode: string;
  photoUrl?: string | null;
  certs: BadgeCert[];
}

interface CertBadgeProps {
  name?: string;
  role?: string;
  companyName?: string;
  photoUrl?: string | null;
  qrCode: string;
  certs?: BadgeCert[];
  snapshot?: BadgeSnapshot | null;
  variant?: 'full' | 'compact';
  print?: boolean;
}

export function badgeUrl(qrCode: string): string {
  return `${window.location.origin}/badge/${encodeURIComponent(qrCode)}`;
}

/**
 * Badge reaudit R2(b): internal badge photos must NOT use the auth-gated
 * /api/users/:id/photo route inside <img> tags — it 404s without a token.
 * When a qrCode exists, rewrite any photo URL to the public QR-scoped route
 * on the API origin (VITE_API_URL), carrying over the cache-bust ?v= param.
 */
export function publicBadgePhotoUrl(qrCode: string, rawPhotoUrl?: string | null): string {
  const apiBase = String(import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/$/, '');
  let v: string | null = null;
  try {
    v = new URL(rawPhotoUrl ?? '', 'http://placeholder.local').searchParams.get('v');
  } catch {
    /* unparseable photo URL — just drop the cache-bust param */
  }
  const u = new URL(`${apiBase}/api/badge/public/${encodeURIComponent(qrCode)}/photo`);
  if (v) u.searchParams.set('v', v);
  return u.toString();
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function isExpired(expirationDate: string | null): boolean {
  if (!expirationDate) return false;
  return new Date(expirationDate).getTime() < Date.now();
}

function fmtDate(d: string | null): string {
  if (!d) return 'No expiration';
  // Badge reaudit R9: calendar-date formatting — never timezone-round-trip a
  // date-only cert expiration (day-shift bug).
  const formatted = formatCalendarDate(d);
  return formatted || '—';
}

export default function CertBadge({
  name,
  role,
  companyName,
  photoUrl,
  qrCode,
  certs,
  snapshot,
  variant = 'full',
  print = false,
}: CertBadgeProps) {
  const resolvedName = name ?? snapshot?.name ?? '';
  const resolvedRole = role ?? snapshot?.role ?? '';
  const resolvedPhoto = (() => {
    const raw = photoUrl ?? snapshot?.photoUrl ?? null;
    if (!raw) return null;
    // R2(b): route internal photos through the public QR-scoped endpoint.
    return qrCode ? publicBadgePhotoUrl(qrCode, raw) : raw;
  })();
  const list: BadgeCert[] = certs ?? snapshot?.certs ?? [];
  const compact = variant === 'compact';

  // Badge reaudit R2(d): if the photo <img> fails to load, hide it and show
  // the initials avatar beneath instead of a broken image.
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => {
    setPhotoFailed(false);
  }, [resolvedPhoto]);

  const border = print ? 'print:border-gray-400' : 'border-gray-200';
  const chipBase = 'inline-block rounded text-[10px] font-bold px-1.5 py-0.5';

  return (
    <div
      data-testid="cert-badge"
      className={`bg-white ${border} rounded-lg p-3 break-inside-avoid print:break-inside-avoid ${
        compact ? 'w-44' : 'w-[220px]'
      }`}
    >
      <div className="flex items-center gap-2">
        {resolvedPhoto && !photoFailed ? (
          <img
            src={resolvedPhoto}
            alt={resolvedName}
            onError={() => setPhotoFailed(true)}
            className={compact ? 'h-9 w-9 rounded-full object-cover' : 'h-12 w-12 rounded-full object-cover'}
          />
        ) : (
          <div
            className={`${
              compact ? 'h-9 w-9 text-xs' : 'h-12 w-12 text-sm'
            } rounded-full bg-slate-200 flex items-center justify-center font-semibold text-slate-600`}
          >
            {initials(resolvedName) || '?'}
          </div>
        )}
        <div className="min-w-0">
          <div className={`font-semibold text-gray-900 text-sm ${compact ? 'truncate' : 'break-words whitespace-normal'}`}>{resolvedName || 'Unknown'}</div>
          {resolvedRole && <div className={`text-xs text-gray-500 ${compact ? 'truncate' : 'break-words whitespace-normal'}`}>{resolvedRole}</div>}
          {companyName && <div className={`text-xs text-gray-400 ${compact ? 'truncate' : 'break-words whitespace-normal'}`}>{companyName}</div>}
        </div>
      </div>

      <div className="mt-2 flex justify-center">
        <QRCodeSVG
          value={badgeUrl(qrCode)}
          size={compact ? 72 : 100}
          data-testid="badge-qr"
          role="img"
          aria-label={`Badge QR code for ${resolvedName}`}
        />
      </div>
      <div className="text-[10px] text-gray-400 text-center mt-1">Scan to verify certifications</div>

      <div className="mt-2 border-t border-gray-100 pt-2" data-testid="badge-cert-list">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Certifications</div>
        {list.length === 0 ? (
          <div className="text-xs text-gray-400">No certifications on file</div>
        ) : (
          <ul className="space-y-1">
            {list.map((c, i) => {
              const expired = isExpired(c.expirationDate);
              return (
                <li key={i} className="text-xs flex items-start justify-between gap-1">
                  <span className="min-w-0">
                    <span className={`text-gray-900 font-medium block ${compact ? 'truncate' : 'break-words whitespace-normal'}`}>{c.certName}</span>
                    <span className="text-gray-500">
                      {c.certType ? `${c.certType} · ` : ''}
                      {fmtDate(c.expirationDate)}
                    </span>
                  </span>
                  {expired && (
                    <span data-testid="expired-chip" className={`${chipBase} bg-red-100 text-red-700 shrink-0`}>
                      EXPIRED
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
